import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

import { MeasurementScale } from './MeasurementScale.js'

// WhiteGreenDot component
const WhiteGreenDot = ({ size = "18px", dotColor = "#00ff00" }) => {
  return (
    <div
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component
const ModelText = ({ text, variant = "right", dotColor = "#00ff00", dotSize = "12px" }) => {
  const isLeft = variant === 'left';

  const formatText = (input) => {
    if (typeof input !== 'string') return input;
    return input.split(/(\d+)/).map((part, i) => 
      /\d+/.test(part) ? <sub key={i} style={{ fontSize: '0.6em', verticalAlign: 'sub' }}>{part}</sub> : part
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isLeft ? 'row-reverse' : 'row',
      alignItems: 'center',
      width: 'max-content',
      transform: isLeft ? 'translateX(-100%)' : 'none'
    }}>
      <div
        style={{
          borderRadius: '50%',
          width: dotSize,
          height: dotSize,
          backgroundColor: dotColor,
          flexShrink: 0
        }}
      />
      <div
        className="model-text"
        style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          whiteSpace: 'nowrap',
          paddingLeft: isLeft ? '0' : '10px',
          paddingRight: isLeft ? '10px' : '0'
        }}
      >
        {formatText(text)}
      </div>
    </div>
  );
};

// Simple noise function to replace open-simplex-noise
const makeNoise3D = (seed = 1) => {
  const random = (x, y, z) => {
    const n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
    return n - Math.floor(n);
  };

  return (x, y, z) => {
    const fx = Math.floor(x);
    const fy = Math.floor(y);
    const fz = Math.floor(z);

    const px = x - fx;
    const py = y - fy;
    const pz = z - fz;

    const n000 = random(fx, fy, fz);
    const n001 = random(fx, fy, fz + 1);
    const n010 = random(fx, fy + 1, fz);
    const n011 = random(fx, fy + 1, fz + 1);
    const n100 = random(fx + 1, fy, fz);
    const n101 = random(fx + 1, fy, fz + 1);
    const n110 = random(fx + 1, fy + 1, fz);
    const n111 = random(fx + 1, fy + 1, fz + 1);

    const smooth = (t) => t * t * (3 - 2 * t);
    const sx = smooth(px);
    const sy = smooth(py);
    const sz = smooth(pz);

    const lerp = (a, b, t) => a + t * (b - a);

    const nx00 = lerp(n000, n100, sx);
    const nx01 = lerp(n001, n101, sx);
    const nx10 = lerp(n010, n110, sx);
    const nx11 = lerp(n011, n111, sx);

    const nxy0 = lerp(nx00, nx10, sy);
    const nxy1 = lerp(nx01, nx11, sy);

    return lerp(nxy0, nxy1, sz) * 2 - 1;
  };
};

export const Inconclusive = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationType = 'No modification',
  surfaceFunctionalizationColor,
  lineColor = "#00ff00",
  baseRotation = [Math.PI / 10, 0, 0],
  isNanoformImageGenerated = false,
  measureScale = 0
}) => {
  const blobRef = useRef();
  const surfaceRef = useRef();
  const modelGroup = useRef();
  const k = 1.3;
  const y = baseRotation[1];

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: coreColor,
        roughness: 0,
        metalness: 0.1,
        opacity: 1,
      }),
    [coreColor]
  );

  const surfaceMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: surfaceFunctionalizationColor,
        roughness: 0,
        metalness: 0.1,
        opacity: 0.35,
        transparent: true,
      }),
    [surfaceFunctionalizationColor]
  );

  // Create geometries with modern BufferGeometry
  const blobGeometry = useMemo(() => new THREE.SphereGeometry(1.5, 100, 100), []);
  const surfaceGeometry = useMemo(() => new THREE.SphereGeometry(1.7, 100, 100), []);

  // Store original positions for animation
  const originalBlobPositions = useMemo(() => {
    return blobGeometry.attributes.position.array.slice();
  }, [blobGeometry]);

  const originalSurfacePositions = useMemo(() => {
    return surfaceGeometry.attributes.position.array.slice();
  }, [surfaceGeometry]);

  const noise = useMemo(() => makeNoise3D(Date.now()), []);

  useFrame(() => {
    const time = performance.now() * 0.0002;

    // Handle model rotation
    if (modelGroup.current) {
      if (isNanoformImageGenerated) {
        modelGroup.current.rotation.y = y;
      } else {
        modelGroup.current.rotation.y += 0.005;
      }
    }

    // Animate blob geometry
    if (blobRef.current) {
      const positions = blobRef.current.geometry.attributes.position.array;
      const originalPositions = originalBlobPositions;

      for (let i = 0; i < positions.length; i += 3) {
        const x = originalPositions[i];
        const y = originalPositions[i + 1];
        const z = originalPositions[i + 2];

        // Normalize and get noise value
        const length = Math.sqrt(x * x + y * y + z * z);
        const nx = x / length;
        const ny = y / length;
        const nz = z / length;

        const noiseValue = noise(nx * k, ny * k, nz * k + time);
        const scale = 1.5 + 0.3 * noiseValue;

        positions[i] = nx * scale;
        positions[i + 1] = ny * scale;
        positions[i + 2] = nz * scale;
      }

      blobRef.current.geometry.attributes.position.needsUpdate = true;
      blobRef.current.geometry.computeVertexNormals();
    }

    // Animate surface geometry
    if (surfaceRef.current && surfaceFunctionalization) {
      const positions = surfaceRef.current.geometry.attributes.position.array;
      const originalPositions = originalSurfacePositions;

      for (let i = 0; i < positions.length; i += 3) {
        const x = originalPositions[i];
        const y = originalPositions[i + 1];
        const z = originalPositions[i + 2];

        // Normalize and get noise value
        const length = Math.sqrt(x * x + y * y + z * z);
        const nx = x / length;
        const ny = y / length;
        const nz = z / length;

        const noiseValue = noise(nx * k, ny * k, nz * k + time);
        const scale = 1.7 + 0.3 * noiseValue;

        positions[i] = nx * scale;
        positions[i + 1] = ny * scale;
        positions[i + 2] = nz * scale;
      }

      surfaceRef.current.geometry.attributes.position.needsUpdate = true;
      surfaceRef.current.geometry.computeVertexNormals();
    }
  });

  return (
    <group position={[0, 0, 0]}>
      {/* Core label */}
      {Boolean(core) && (
        <group>
          <Html className="core" position={[1, 0.95, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0, 2],
              [0.6, 0.95, 2],
              [1, 0.95, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group>
          <Html className="core--left" position={[-0.9, 1.05, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={surfaceFunctionalizationType}
              variant="left"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[-0.05, 0.77, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="18px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.05, 0.77, 2],
              [-0.4, 1, 2],
              [-0.9, 1, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}

      <MeasurementScale width={measureScale} unit="nm" color="#425a70" />

      {/* 3D Model */}
      <group ref={modelGroup} rotation={baseRotation}>
        {/* Core blob */}
        <mesh ref={blobRef} geometry={blobGeometry} material={material} />

        {/* Surface blob */}
        {Boolean(surfaceFunctionalization) && (
          <mesh ref={surfaceRef} geometry={surfaceGeometry} material={surfaceMaterial} />
        )}
      </group>
    </group>
  );
};